import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import VoiceCallSounds from './voiceCallSounds';
import { SecureAPI } from '../config.js';

// ✅ VOICE CONFIG - COPY FROM AudioPodcastModal
const VOICE_CONFIG = {
  'Host 1': '33045fd9-8010-43f6-b6b0-da3fbf326c29',
  'host 1': '33045fd9-8010-43f6-b6b0-da3fbf326c29',
};
class BigCalendarPlannerVoiceManager {
constructor(
  selectedVoice,
  onTranscriptUpdate,
  onWaveformUpdate,
  onStatusUpdate,
  userName,
  calendarData,
  onCalendarUpdate,
  notificationPerm,
  scheduleNotif,
  firestoreService
) {
  // ✅ Store callbacks properly
  this.selectedVoice = selectedVoice;
  this.onTranscriptUpdate = onTranscriptUpdate;
  this.onWaveformUpdate = onWaveformUpdate;
  this.onStatusUpdate = onStatusUpdate;
  this.userName = userName;
  this.calendarData = calendarData;
  this.onCalendarUpdate = onCalendarUpdate;
  this.notificationPerm = notificationPerm;
  this.scheduleNotif = scheduleNotif;
  this.firestoreService = firestoreService;

  this.isConnected = false;
  this.isListening = false;
  this.isInitializing = false;
  this.isMuted = false;
  this.isPlayingAudio = false;
  this.isAISpeaking = false;
  this.pendingToolCall = false;

  this.audioQueue = [];
  this.currentAudioElement = null;

  this.audioContext = null;
  this.analyser = null;
  this.dataArray = null;
  this.animationFrameId = null;
  this.audioStream = null;

  this.audioSpeed = 1.05;
  this.callSounds = new VoiceCallSounds();

  this.silenceTimer = null;
  this.messageHistory = [];
  this.lastToolCallTime = null;
  
  // ✅ Hume API config (stays here for TTS)
// ✅ ADD THIS INSTEAD:
this.voiceId = "33045fd9-8010-43f6-b6b0-da3fbf326c29"; // Just store the ID
}
  // ✅ NEW METHOD: Force reload calendar from Firestore
  async forceReloadCalendar() {
    try {
      if (!this.firestoreService?.isReady?.()) {
        console.warn('⚠️ Firestore not ready for reload');
        return this.calendarData;
      }

      console.log('🔄 Voice: Reloading calendar from Firestore...');
      
      const { loadCalendarFromStorage } = await import('./schedulerHandlers.js');
      const freshCalendar = await loadCalendarFromStorage(this.firestoreService);
      
      this.calendarData = freshCalendar;
      
      console.log('✅ Voice: Calendar reloaded:', {
        dates: Object.keys(freshCalendar).length,
        totalEvents: Object.values(freshCalendar).reduce((sum, events) => sum + events.length, 0)
      });
      
      return freshCalendar;
    } catch (error) {
      console.error('❌ Voice: Failed to reload calendar:', error);
      return this.calendarData;
    }
  }

  updateCalendarData(newCalendarData) {
    this.calendarData = newCalendarData;
    console.log('📅 Voice manager calendar data updated');
  }

async initialize() {
  try {
    this.isInitializing = true;
    this.onStatusUpdate?.('Starting...');

    console.log('🎤 Initializing Azure Speech Recognition...');

    // ✅ NEW: Get token from SecureAPI instead of using hardcoded keys
    const tokenData = await SecureAPI.getAzureSpeechToken();
    
    // ✅ Use token-based auth instead of subscription key
    this.speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
      tokenData.token,
      tokenData.region
    );

    this.speechConfig.speechRecognitionLanguage = 'en-US';
    this.speechConfig.setProperty(
      SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
      '5000'
    );

    this.onStatusUpdate?.('One sec...');

    this.audioStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });

    await this.setupWaveform();

    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    this.recognizer = new SpeechSDK.SpeechRecognizer(this.speechConfig, audioConfig);

    this.setupRecognitionHandlers();

    this.isConnected = true;
    this.isInitializing = false;

    console.log('✅ Azure Speech ready');
    this.onStatusUpdate?.('Ready to listen');

    // ✅ ADD THIS - Play start sound when initialized
    console.log('🔊 Playing call start sound...');
    if (this.callSounds) {
      await this.callSounds.playStartSound();
    }

    return true;
  } catch (error) {
    console.error('❌ Init failed:', error);
    this.isInitializing = false;
    this.onStatusUpdate?.('Failed to initialize');
    throw error;
  }
}


  async setupWaveform() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      const source = this.audioContext.createMediaStreamSource(this.audioStream);
      source.connect(this.analyser);

      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      console.log('✅ Waveform ready');
    } catch (error) {
      console.error('⚠️ Waveform setup failed:', error);
    }
  }

  animateWaveform() {
    if (!this.isListening || !this.analyser || !this.dataArray) {
      return;
    }

    if (this.isMuted) {
      this.animationFrameId = requestAnimationFrame(() => this.animateWaveform());
      return;
    }

    this.analyser.getByteFrequencyData(this.dataArray);

    const points = [];
    const totalPoints = 60;
    const width = 600;
    const height = 50;
    const centerY = height / 2;

    for (let i = 0; i < totalPoints; i++) {
      const idx = Math.floor((i / totalPoints) * this.dataArray.length);
      const freq = this.dataArray[idx] / 255;
      const x = (i / totalPoints) * width;

      const waveHeight = Math.pow(freq, 1.5) * 15;
      const angle = (i / totalPoints) * Math.PI * 2;
      const wobble = Math.sin(angle) * waveHeight;

      const y = centerY - wobble;
      points.push(`${x},${y}`);
    }

    this.onWaveformUpdate?.({
      points: points.join(' '),
      isActive: true,
      height: Math.max(...this.dataArray) / 255 * 50,
      frequencyData: Array.from(this.dataArray)
    });

    this.animationFrameId = requestAnimationFrame(() => this.animateWaveform());
  }

  async startListening() {
    try {
      if (!this.isConnected) {
        await this.initialize();
      }

      console.log('🎤 Starting continuous recognition...');
      this.recognizer.startContinuousRecognitionAsync(
        () => {
          console.log('✅ Continuous recognition started');
          this.isListening = true;
          this.animateWaveform();
        },
        (error) => {
          console.error('❌ Start failed:', error);
          this.isListening = false;
        }
      );
    } catch (error) {
      console.error('❌ Listen error:', error);
      throw error;
    }
  }

  stopListening() {
    if (this.recognizer) {
      console.log('🛑 Stopping recognition...');
      this.recognizer.stopContinuousRecognitionAsync(
        () => {
          console.log('✅ Stopped');
          this.isListening = false;
        },
        (error) => {
          console.error('❌ Stop error:', error);
        }
      );
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.onWaveformUpdate?.({
      points: '',
      isActive: false,
      height: 0
    });
  }
setupRecognitionHandlers() {
  this.recognizer.recognizing = (s, e) => {
    if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
      const text = e.result.text;
      if (text.trim()) {
        console.log('🎤 User speaking (interim):', text);

        // ✅ CRITICAL: INTERRUPT AI AUDIO WHEN USER STARTS SPEAKING
        if (this.isAISpeaking) {
          console.log('🛑 User interrupting - stopping AI audio');
          this.interruptCurrentAudio();
          this.audioQueue = [];
          this.isAISpeaking = false;
          this.isPlayingAudio = false;
          console.log('✅ AI audio stopped, ready for new input');
        }

        this.onTranscriptUpdate?.({
          type: 'user',
          text: text,
          isFinal: false
        });
      }
    }
  };

  this.recognizer.recognized = (s, e) => {
    if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
      const text = e.result.text;
      if (text.trim()) {
        console.log('✅ Final user message:', text);

        // ✅ ALSO INTERRUPT HERE JUST TO BE SAFE
        if (this.isAISpeaking) {
          console.log('🛑 Interrupting AI for final message');
          this.interruptCurrentAudio();
          this.audioQueue = [];
          this.isAISpeaking = false;
          this.isPlayingAudio = false;
        }

        this.onTranscriptUpdate?.({
          type: 'user',
          text: text,
          isFinal: true
        });

        if (this.messageHistory.length > 10) {
          this.messageHistory = this.messageHistory.slice(-10);
        }

        this.scheduleResponseGeneration(text);
      }
    }
  };

  this.recognizer.sessionStarted = (s, e) => {
    console.log('🎤 Session started');
    this.isListening = true;
  };

  this.recognizer.sessionStopped = (s, e) => {
    console.log('🎤 Session stopped');
    this.isListening = false;
  };

  this.recognizer.canceled = (s, e) => {
    console.error('❌ Canceled:', e.errorDetails);
    this.isListening = false;
  };
}
scheduleResponseGeneration(userMessage) {
  // Clear existing timer
  if (this.silenceTimer) {
    clearTimeout(this.silenceTimer);
  }

  // ✅ CRITICAL: INTERRUPT AI AUDIO WHEN USER SPEAKS AGAIN
  if (this.isAISpeaking) {
    console.log('🛑 User interrupting - stopping AI audio immediately');
    this.interruptCurrentAudio();
    this.audioQueue = [];
    this.isAISpeaking = false;
    this.isPlayingAudio = false;
    this.pendingToolCall = false;
    console.log('✅ Ready to process new message');
  }

  // Don't process if AI or tool is busy
  if (this.isAISpeaking || this.pendingToolCall) {
    console.log('⏸️ AI busy, waiting for silence...');
    return;
  }

  // Wait 800ms of silence before processing
  this.silenceTimer = setTimeout(() => {
    console.log('💬 Processing user message after silence...');
    this.generateAndStreamResponse(userMessage);
  }, 800);
}
parseRelativeDate(dateString) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (!dateString) return today;
  
  const lower = dateString.toLowerCase().trim();
  
  // TODAY
  if (lower === 'today' || lower === '') return today;
  
  // TOMORROW
  if (lower === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  }
  
  // NEXT [DAY] - e.g., "next friday"
  const nextDayMatch = lower.match(/next (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  if (nextDayMatch) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDay = days.indexOf(nextDayMatch[1].toLowerCase());
    const currentDay = today.getDay();
    let daysToAdd = targetDay - currentDay;
    if (daysToAdd <= 0) daysToAdd += 7;
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysToAdd);
    return nextDate;
  }
  
  // SPECIFIC DAY OF WEEK (this week) - e.g., "friday"
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayIndex = days.findIndex(day => lower === day);
  if (dayIndex !== -1) {
    const currentDay = today.getDay();
    let daysToAdd = dayIndex - currentDay;
    
    // If day already passed this week, schedule for next week
    if (daysToAdd < 0) daysToAdd += 7;
    
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysToAdd);
    return targetDate;
  }
  
  // TRY TO PARSE AS DATE STRING
  const parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) {
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }
  
  // DEFAULT TO TODAY
  return today;
}
parseRecurringPattern(description) {
  const text = description.toLowerCase();
  
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayMatch = days.find(day => text.includes(`every ${day}`) || text.includes(`on ${day}s`));
  
  if (dayMatch) {
    return {
      type: 'weekly',
      dayOfWeek: days.indexOf(dayMatch),
      dayName: dayMatch,
      interval: 1
    };
  }
  
  if (text.includes('every day') || text.includes('daily')) {
    return { type: 'daily', interval: 1 };
  }
  
  return null;
}

formatDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

generateRecurringInstances(baseEvent, pattern, startDate, weeksAhead = 8) {
  const instances = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  
  const endDate = new Date(current);
  endDate.setDate(endDate.getDate() + (weeksAhead * 7));
  
  if (pattern.type === 'weekly') {
    while (current <= endDate) {
      if (current.getDay() === pattern.dayOfWeek) {
        const dateKey = this.formatDateKey(current);
        instances.push({
          dateKey,
          event: { ...baseEvent, isRecurring: true, instanceDate: dateKey }
        });
      }
      current.setDate(current.getDate() + 1);
    }
  }
  
  return instances;
}

  formatTimeNaturally(timeStr) {
    if (!timeStr || timeStr === 'All day') return 'all day';
    
    const timeMatch = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)/i);
    if (!timeMatch) return timeStr;
    
    const hours = parseInt(timeMatch[1]);
    const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const meridian = timeMatch[3].toLowerCase();
    
    if (minutes === 0) {
      return `${hours}${meridian}`;
    } else {
      return `${hours}:${minutes.toString().padStart(2, '0')}${meridian}`;
    }
  }

getCalendarContext() {
  if (!this.calendarData || Object.keys(this.calendarData).length === 0) {
    return 'No events scheduled.';
  }

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  
  // ✅ NEW: Calculate date ranges
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  const lastWeekStart = new Date(today);
  lastWeekStart.setDate(today.getDate() - 7);
  
  const nextWeekEnd = new Date(today);
  nextWeekEnd.setDate(today.getDate() + 14);
  
  let context = 'CURRENT CALENDAR:\n';
  
  // ✅ Group events by time period
  const pastEvents = [];
  const todayEvents = [];
  const tomorrowEvents = [];
  const thisWeekEvents = [];
  const nextWeekEvents = [];
  const futureEvents = [];
  
  Object.entries(this.calendarData).forEach(([dateKey, events]) => {
    const [year, month, day] = dateKey.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);
    eventDate.setHours(0, 0, 0, 0);
    
    const dateLabel = eventDate.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
    
    events.forEach(event => {
      const eventInfo = {
        dateKey,
        date: eventDate,
        dateLabel,
        ...event
      };
      
      if (eventDate < yesterday) {
        pastEvents.push(eventInfo);
      } else if (eventDate.getTime() === today.getTime()) {
        todayEvents.push(eventInfo);
      } else if (eventDate.getTime() === tomorrow.getTime()) {
        tomorrowEvents.push(eventInfo);
      } else if (eventDate > today && eventDate <= new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) {
        thisWeekEvents.push(eventInfo);
      } else if (eventDate > today && eventDate <= nextWeekEnd) {
        nextWeekEvents.push(eventInfo);
      } else {
        futureEvents.push(eventInfo);
      }
    });
  });
  
  // ✅ Format TODAY's events with time context
  if (todayEvents.length > 0) {
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinutes;
    
    const past = [];
    const current = [];
    const upcoming = [];
    
    todayEvents.forEach(event => {
      if (!event.time || event.time === 'All day') {
        upcoming.push(event);
        return;
      }
      
      const timeMatch = event.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const meridian = timeMatch[3].toUpperCase();
        
        if (meridian === 'PM' && hours !== 12) hours += 12;
        if (meridian === 'AM' && hours === 12) hours = 0;
        
        const eventTimeInMinutes = hours * 60 + minutes;
        const timeDiff = eventTimeInMinutes - currentTimeInMinutes;
        
        if (timeDiff < -30) past.push(event);
        else if (timeDiff >= -30 && timeDiff <= 30) current.push(event);
        else upcoming.push(event);
      }
    });
    
    context += `\n📅 TODAY (${today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}):\n`;
    
    if (past.length > 0) {
      context += '\n  PAST:\n';
      past.forEach(e => {
        const status = e.completed ? '✓ completed' : '✗ missed';
        context += `    ${status} - ${e.text} at ${this.formatTimeNaturally(e.time)}\n`;
      });
    }
    
    if (current.length > 0) {
      context += '\n  HAPPENING NOW:\n';
      current.forEach(e => {
        context += `    ⏰ ${e.text} at ${this.formatTimeNaturally(e.time)}\n`;
      });
    }
    
    if (upcoming.length > 0) {
      context += '\n  UPCOMING:\n';
      upcoming.forEach(e => {
        const timeStr = e.time && e.time !== 'All day' 
          ? ` at ${this.formatTimeNaturally(e.time)}` 
          : '';
        context += `    ○ ${e.text}${timeStr}\n`;
      });
    }
  } else {
    context += `\n📅 TODAY: No events\n`;
  }
  
  // ✅ TOMORROW
  if (tomorrowEvents.length > 0) {
    context += `\n📅 TOMORROW (${tomorrow.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}):\n`;
    tomorrowEvents.forEach(e => {
      const timeStr = e.time && e.time !== 'All day' 
        ? ` at ${this.formatTimeNaturally(e.time)}` 
        : '';
      context += `  - ${e.text}${timeStr}\n`;
    });
  }
  
  // ✅ THIS WEEK (next 7 days)
  if (thisWeekEvents.length > 0) {
    context += `\n📅 THIS WEEK:\n`;
    
    // Group by date
    const byDate = {};
    thisWeekEvents.forEach(e => {
      if (!byDate[e.dateLabel]) byDate[e.dateLabel] = [];
      byDate[e.dateLabel].push(e);
    });
    
    Object.entries(byDate).forEach(([dateLabel, events]) => {
      context += `  ${dateLabel}:\n`;
      events.forEach(e => {
        const timeStr = e.time && e.time !== 'All day' 
          ? ` at ${this.formatTimeNaturally(e.time)}` 
          : '';
        context += `    - ${e.text}${timeStr}\n`;
      });
    });
  }
  
  // ✅ NEXT WEEK (8-14 days)
  if (nextWeekEvents.length > 0) {
    context += `\n📅 NEXT WEEK:\n`;
    
    const byDate = {};
    nextWeekEvents.forEach(e => {
      if (!byDate[e.dateLabel]) byDate[e.dateLabel] = [];
      byDate[e.dateLabel].push(e);
    });
    
    Object.entries(byDate).forEach(([dateLabel, events]) => {
      context += `  ${dateLabel}:\n`;
      events.forEach(e => {
        const timeStr = e.time && e.time !== 'All day' 
          ? ` at ${this.formatTimeNaturally(e.time)}` 
          : '';
        context += `    - ${e.text}${timeStr}\n`;
      });
    });
  }
  
  // ✅ LAST WEEK (for reference)
  if (pastEvents.length > 0) {
    const recentPast = pastEvents.filter(e => e.date >= lastWeekStart);
    if (recentPast.length > 0) {
      context += `\n📅 LAST WEEK (for reference):\n`;
      
      const byDate = {};
      recentPast.forEach(e => {
        if (!byDate[e.dateLabel]) byDate[e.dateLabel] = [];
        byDate[e.dateLabel].push(e);
      });
      
      Object.entries(byDate).forEach(([dateLabel, events]) => {
        context += `  ${dateLabel}:\n`;
        events.forEach(e => {
          const status = e.completed ? '✓' : '✗';
          const timeStr = e.time && e.time !== 'All day' 
            ? ` at ${this.formatTimeNaturally(e.time)}` 
            : '';
          context += `    ${status} ${e.text}${timeStr}\n`;
        });
      });
    }
  }
  
  return context;
}

async executeCalendarTool(functionName, functionArgs) {
  try {
    console.log('⚙️ Executing tool:', functionName, functionArgs);

    // ✅ CRITICAL: Reload calendar FIRST
    await this.forceReloadCalendar();
    
    if (functionName === 'schedule_event') {
      const eventDate = this.parseRelativeDate(functionArgs.date || 'today');
      const dateKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
      
      let formattedTime = functionArgs.time || 'All day';
      
      if (formattedTime && formattedTime !== 'All day') {
        const timeMatch = formattedTime.match(/^(\d{1,2}):(\d{2})$/);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1]);
          const minutes = timeMatch[2];
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          formattedTime = `${hours}:${minutes} ${ampm}`;
        }
      }
      
      // ✅ CHECK FOR RECURRING PATTERN
      const fullDescription = `${functionArgs.title} ${functionArgs.description || ''}`.toLowerCase();
      const recurringPattern = this.parseRecurringPattern(fullDescription);
      
      let updatedCalendar;
      let instanceCount = 1;
      
      if (recurringPattern) {
        console.log('🔄 Detected recurring event:', recurringPattern);
        
        const recurrenceId = `recurring_${Date.now()}`;
        const baseEvent = {
          text: functionArgs.title,
          time: formattedTime,
          description: functionArgs.description || '',
          completed: false,
          notifyEnabled: true,
          notificationId: null,
          isRecurring: true,
          recurrenceId: recurrenceId,
          recurrencePattern: recurringPattern,
          createdAt: new Date().toISOString()
        };
        
        const instances = this.generateRecurringInstances(baseEvent, recurringPattern, eventDate, 8);
        instanceCount = instances.length;
        console.log(`✅ Generated ${instanceCount} recurring instances`);
        
        updatedCalendar = { ...this.calendarData };
        
        instances.forEach(({ dateKey: instDateKey, event }) => {
          updatedCalendar[instDateKey] = [...(updatedCalendar[instDateKey] || []), event];
        });
        
      } else {
        // Single event
        const newEvent = {
          text: functionArgs.title,
          time: formattedTime,
          description: functionArgs.description || '',
          completed: false,
          notifyEnabled: true,
          notificationId: null,
          createdAt: new Date().toISOString()
        };

        updatedCalendar = {
          ...this.calendarData,
          [dateKey]: [...(this.calendarData[dateKey] || []), newEvent]
        };
      }

      console.log('⏳ Updating calendar via callback...');
      // ✅ CRITICAL: Use the callback to update
      await this.onCalendarUpdate(updatedCalendar);
      
      // ✅ CRITICAL: Update local copy
      this.calendarData = updatedCalendar;
      console.log('✅ Calendar updated successfully');

      const timeFormatted = this.formatTimeNaturally(formattedTime);
      
      if (recurringPattern) {
        const dayName = recurringPattern.dayName || 'day';
        return {
          success: true,
          message: `Set up recurring event: "${functionArgs.title}" every ${dayName} at ${timeFormatted}. Created ${instanceCount} instances.`
        };
      } else {
        return {
          success: true,
          message: `Added "${functionArgs.title}" to ${eventDate.toLocaleDateString('en-US', { weekday: 'long' })} at ${timeFormatted}`
        };
      }
    }

    if (functionName === 'get_schedule') {
      const queryDate = this.parseRelativeDate(functionArgs.date || 'today');
      const dateKey = `${queryDate.getFullYear()}-${String(queryDate.getMonth() + 1).padStart(2, '0')}-${String(queryDate.getDate()).padStart(2, '0')}`;
      
      const events = this.calendarData[dateKey] || [];

      return {
        success: true,
        date: queryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
        eventCount: events.length,
        events: events.map(e => ({
          title: e.text,
          time: this.formatTimeNaturally(e.time || 'All day'),
          completed: e.completed
        }))
      };
    }

    if (functionName === 'update_event') {
      let found = null;
      let foundDateKey = null;

      for (const [dateKey, events] of Object.entries(this.calendarData)) {
        const eventIndex = events.findIndex(e => 
          e.text.toLowerCase().includes(functionArgs.eventTitle.toLowerCase()) ||
          functionArgs.eventTitle.toLowerCase().includes(e.text.toLowerCase())
        );

        if (eventIndex !== -1) {
          found = { event: events[eventIndex], index: eventIndex };
          foundDateKey = dateKey;
          break;
        }
      }

      if (!found) {
        return { success: false, message: 'Event not found' };
      }

      let newTime = functionArgs.newTime || found.event.time;
      if (newTime && newTime !== 'All day') {
        const timeMatch = newTime.match(/^(\d{1,2}):(\d{2})$/);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1]);
          const minutes = timeMatch[2];
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          newTime = `${hours}:${minutes} ${ampm}`;
        }
      }

      const updatedEvent = {
        ...found.event,
        text: functionArgs.newTitle || found.event.text,
        time: newTime,
        description: functionArgs.newDescription || found.event.description
      };

      const updatedCalendar = { ...this.calendarData };
      
      updatedCalendar[foundDateKey] = updatedCalendar[foundDateKey].filter((_, idx) => idx !== found.index);
      if (updatedCalendar[foundDateKey].length === 0) delete updatedCalendar[foundDateKey];

      const targetDateKey = functionArgs.newDate 
        ? (() => {
            const d = this.parseRelativeDate(functionArgs.newDate);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          })()
        : foundDateKey;

      updatedCalendar[targetDateKey] = [...(updatedCalendar[targetDateKey] || []), updatedEvent];

      console.log('⏳ Updating calendar via callback...');
      // ✅ CRITICAL: Use the callback
      await this.onCalendarUpdate(updatedCalendar);
      
      // ✅ CRITICAL: Update local copy
      this.calendarData = updatedCalendar;
      console.log('✅ Event updated successfully');

      return {
        success: true,
        message: 'Event updated successfully'
      };
    }

    if (functionName === 'delete_event') {
      let found = null;
      let foundDateKey = null;

      for (const [dateKey, events] of Object.entries(this.calendarData)) {
        const eventIndex = events.findIndex(e => 
          e.text.toLowerCase().includes(functionArgs.eventTitle.toLowerCase()) ||
          functionArgs.eventTitle.toLowerCase().includes(e.text.toLowerCase())
        );

        if (eventIndex !== -1) {
          found = { event: events[eventIndex], index: eventIndex };
          foundDateKey = dateKey;
          break;
        }
      }

      if (!found) {
        return { success: false, message: 'Event not found' };
      }

      const updatedCalendar = { ...this.calendarData };
      updatedCalendar[foundDateKey] = updatedCalendar[foundDateKey].filter((_, idx) => idx !== found.index);
      
      if (updatedCalendar[foundDateKey].length === 0) {
        delete updatedCalendar[foundDateKey];
      }

      console.log('⏳ Updating calendar via callback...');
      // ✅ CRITICAL: Use the callback
      await this.onCalendarUpdate(updatedCalendar);
      
      // ✅ CRITICAL: Update local copy
      this.calendarData = updatedCalendar;
      console.log('✅ Event deleted successfully');

      return {
        success: true,
        message: `Deleted "${found.event.text}"`
      };
    }

    return { success: false, message: 'Unknown tool' };

  } catch (error) {
    console.error('❌ Tool execution error:', error);
    return { success: false, message: error.message };
  }
}
async generateAndStreamResponse(userMessage) {
  try {
    console.log('🎙️ Processing voice input...');

    // ✅ INTERRUPT PREVIOUS RESPONSE IF STILL PLAYING
    if (this.isAISpeaking) {
      console.log('🛑 Interrupting previous AI response');
      this.interruptCurrentAudio();
      this.audioQueue = [];
      this.isAISpeaking = false;
      this.isPlayingAudio = false;
    }

    if (this.pendingToolCall) {
      console.log('⏸️ Tool call already in progress, ignoring');
      return;
    }

    // ✅ ADD SAFETY CHECK
    if (!userMessage || !userMessage.trim()) {
      console.warn('⚠️ Empty message, ignoring');
      return;
    }

    this.messageHistory.push({
      role: 'user',
      content: userMessage
    });

    if (this.messageHistory.length > 10) {
      this.messageHistory = this.messageHistory.slice(-10);
    }

    this.isAISpeaking = true;

    this.onTranscriptUpdate?.({
      type: 'assistant',
      text: '',
      isFinal: false,
      isLoading: true
    });

    const response = await this.getAzureResponseWithTools(userMessage);
    
    // ✅ ADD NULL SAFETY
    if (!response || !response.choices || !response.choices[0]) {
      console.error('❌ Invalid response from Azure');
      this.isAISpeaking = false;
      this.pendingToolCall = false;
      
      this.onTranscriptUpdate?.({
        type: 'assistant',
        text: 'Sorry, I didn\'t get a response',
        isFinal: true
      });
      return;
    }

    const choice = response.choices[0];

if (choice.finish_reason === 'tool_calls' && choice.message?.tool_calls) {
  this.pendingToolCall = true;
  
  const toolCall = choice.message.tool_calls[0];
  const toolCallId = toolCall.id;
  const functionName = toolCall.function.name;
  let functionArgs = JSON.parse(toolCall.function.arguments);

  // ✅ ENHANCE: If deleting/updating event with vague reference, try to identify it
  if ((functionName === 'delete_event' || functionName === 'update_event') && 
      functionArgs.eventTitle && functionArgs.eventTitle.length < 10) {
    
    const identified = this.identifyEventFromContext(userMessage);
    if (identified) {
      console.log('✅ Identified event from context:', identified.text);
      functionArgs.eventTitle = identified.text;
      
      // Update the tool call
      toolCall.function.arguments = JSON.stringify(functionArgs);
    }
  }

  console.log('🔧 Tool call:', functionName, functionArgs);

        this.messageHistory.push({
          role: 'assistant',
          content: null,
          tool_calls: [toolCall]
        });

        this.isAISpeaking = false;

        this.onTranscriptUpdate?.({
          type: 'assistant',
          text: '',
          isFinal: false,
          isLoading: false
        });

        // ✅ FIX: AWAIT the tool execution and capture the result
        console.log('⚙️ Executing tool:', functionName);
        const toolResult = await this.executeCalendarTool(functionName, functionArgs);
        console.log('✅ Tool result:', toolResult);

        this.lastToolCallTime = Date.now();

        this.messageHistory.push({
          role: 'tool',
          tool_call_id: toolCallId,
          name: functionName,
          content: JSON.stringify(toolResult)
        });

        // ✅ FIX: Wait a bit longer to ensure calendar is updated
        await new Promise(resolve => setTimeout(resolve, 500));

        // ✅ FIX: Now get confirmation with fresh calendar data
// ✅ FIX: Now get confirmation with fresh calendar data
try {
  console.log('🤖 Getting AI confirmation...');
  
  const confirmResponse = await this.getAzureResponseWithoutTools();
  const aiConfirmation = confirmResponse.choices[0].message.content.trim();
  
  console.log('✅ AI says:', aiConfirmation);
  
  this.messageHistory.push({
    role: 'assistant',
    content: aiConfirmation
  });
  
  if (this.messageHistory.length > 10) {
    this.messageHistory = this.messageHistory.slice(-10);
  }
  
  this.onTranscriptUpdate?.({
    type: 'assistant',
    text: aiConfirmation,
    isFinal: true
  });
  
  // ✅ CRITICAL: Make sure we're playing audio
  this.isAISpeaking = true;
  this.isPlayingAudio = true;  // ← ADD THIS!
  
  await this.streamAudioChunks(aiConfirmation, this.selectedVoice);
  
} catch (error) {
  console.error('❌ Confirmation error:', error);
} finally {
  this.isAISpeaking = false;
  this.pendingToolCall = false;
}

        return;
      }

      const aiResponse = choice.message?.content;

      if (!aiResponse) {
        console.error('❌ No response');
        this.isAISpeaking = false;
        this.pendingToolCall = false;
        return;
      }

      console.log('✅ Response:', aiResponse);

      this.messageHistory.push({
        role: 'assistant',
        content: aiResponse
      });

      this.onTranscriptUpdate?.({
        type: 'assistant',
        text: aiResponse,
        isFinal: true,
        isLoading: false
      });

      await this.streamAudioChunks(aiResponse, this.selectedVoice);

      this.isAISpeaking = false;
      this.pendingToolCall = false;

    } catch (error) {
      console.error('❌ Error:', error);
      this.isAISpeaking = false;
      this.pendingToolCall = false;

      this.onTranscriptUpdate?.({
        type: 'assistant',
        text: 'Sorry, I encountered an error',
        isFinal: true
      });
    }
  }
identifyEventFromContext(userQuery) {
  if (!this.calendarData || Object.keys(this.calendarData).length === 0) {
    console.log('⚠️ No events to search');
    return null;
  }

  const query = userQuery.toLowerCase();
  console.log('🔍 Searching for event:', query);

  // ✅ STEP 1: Collect ALL events with metadata
  const allEvents = [];
  
  Object.entries(this.calendarData).forEach(([dateKey, events]) => {
    const [year, month, day] = dateKey.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);
    
    events.forEach((event, idx) => {
      allEvents.push({
        ...event,
        dateKey,
        index: idx,
        date: eventDate,
        dayOfWeek: eventDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase(),
        dayName: eventDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase(),
        timeMinutes: this.parseTimeToMinutes(event.time)
      });
    });
  });

  console.log(`📋 Searching ${allEvents.length} events`);

  // ✅ STEP 2: Try EXACT title match FIRST
  const exactMatch = allEvents.find(e => 
    e.text.toLowerCase() === query.trim() ||
    e.text.toLowerCase().includes(query.trim())
  );
  
  if (exactMatch) {
    console.log('✅ Found exact title match:', exactMatch.text);
    return exactMatch;
  }

  // ✅ STEP 3: Try matching by keywords (longer phrases)
  const keywords = query.split(/\s+/).filter(w => w.length > 2);
  if (keywords.length > 0) {
    const keywordMatch = allEvents.find(e => {
      const eventTitle = e.text.toLowerCase();
      // Match if ALL keywords are in title
      return keywords.every(kw => eventTitle.includes(kw));
    });
    
    if (keywordMatch) {
      console.log('✅ Found keyword match:', keywordMatch.text);
      return keywordMatch;
    }
  }

  // ✅ STEP 4: Try matching by day of week
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const foundDay = days.find(day => query.includes(day));
  
  if (foundDay) {
    const dayMatches = allEvents.filter(e => e.dayOfWeek === foundDay);
    
    if (dayMatches.length === 1) {
      console.log('✅ Found by day:', dayMatches[0].text);
      return dayMatches[0];
    } else if (dayMatches.length > 1) {
      // Try to narrow by time
      const timeMatch = query.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
      if (timeMatch) {
        const queryTime = this.parseTimeToMinutes(
          timeMatch[3] 
            ? `${timeMatch[1]}:${timeMatch[2] || '00'} ${timeMatch[3]}`
            : `${timeMatch[1]}:${timeMatch[2] || '00'}`
        );
        
        if (queryTime !== null) {
          const match = dayMatches.find(e => 
            e.timeMinutes && Math.abs(e.timeMinutes - queryTime) < 30
          );
          if (match) {
            console.log('✅ Found by day + time:', match.text);
            return match;
          }
        }
      }
      
      // Return first if can't narrow
      console.log('✅ Returning first match for day:', dayMatches[0].text);
      return dayMatches[0];
    }
  }

  // ✅ STEP 5: Try matching by time alone
  const timeMatch = query.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (timeMatch) {
    const queryTime = this.parseTimeToMinutes(
      timeMatch[3] 
        ? `${timeMatch[1]}:${timeMatch[2] || '00'} ${timeMatch[3]}`
        : `${timeMatch[1]}:${timeMatch[2] || '00'}`
    );
    
    if (queryTime !== null) {
      const timeOnlyMatch = allEvents.find(e => 
        e.timeMinutes && Math.abs(e.timeMinutes - queryTime) < 30
      );
      
      if (timeOnlyMatch) {
        console.log('✅ Found by time:', timeOnlyMatch.text);
        return timeOnlyMatch;
      }
    }
  }

  console.log('❌ No event found matching:', query);
  return null;
}

parseTimeToMinutes(timeStr) {
  if (!timeStr || timeStr === 'All day') return null;
  
  const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (!match) return null;
  
  let hours = parseInt(match[1]);
  const minutes = match[2] ? parseInt(match[2]) : 0;
  const meridian = match[3]?.toUpperCase();
  
  if (meridian === 'PM' && hours !== 12) hours += 12;
  if (meridian === 'AM' && hours === 12) hours = 0;
  
  return hours * 60 + minutes;
}

  async streamAudioChunks(fullText, voiceId) {
    try {
      const chunks = this.splitIntoChunks(fullText, 15);
      console.log(`🎵 Streaming ${chunks.length} chunks`);

      for (const chunk of chunks) {
        if (!chunk.trim()) continue;

        const audioBlob = await this.convertTextToSpeech(chunk, voiceId);

        if (audioBlob) {
          this.queueAudioForPlayback(audioBlob);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log('✅ All chunks queued for playback');
    } catch (error) {
      console.error('❌ Stream error:', error);
    }
  }

  splitIntoChunks(text, wordsPerChunk = 15) {
    const sentences = text.match(/[^.!?]+[.!?]+/g);

    if (sentences && sentences.length > 0) {
      return sentences.map(s => s.trim());
    }

    const words = text.split(/\s+/);
    const chunks = [];

    for (let i = 0; i < words.length; i += wordsPerChunk) {
      chunks.push(words.slice(i, i + wordsPerChunk).join(' '));
    }

    return chunks;
  }
async convertTextToSpeech(text, voiceId) {
  try {
    console.log(`🎙️ Converting to speech (secure): "${text.substring(0, 50)}..."`);

    const audioSpeed = Math.max(0.5, Math.min(2.0, this.audioSpeed));

    // ✅ MAP VOICE NAME TO ID
    const voiceIdForHume = VOICE_CONFIG[voiceId] || VOICE_CONFIG['Host 1'];
    
    console.log('🎤 Using voice:', voiceId, '→', voiceIdForHume);

    // ✅ Use SecureAPI with correct voice ID
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      voiceIdForHume,  // ← CHANGED: Use mapped ID
      audioSpeed
    );

    console.log('✅ TTS blob received:', audioBlob.size, 'bytes');
    return audioBlob;

  } catch (error) {
    console.error(`❌ TTS error:`, error);
    return null;
  }
}

queueAudioForPlayback(audioBlob) {
  this.audioQueue.push(audioBlob);

  // ✅ IMPORTANT: Set flag BEFORE calling playNextAudio
  if (!this.isPlayingAudio) {
    this.isPlayingAudio = true;  
    this.playNextAudio();
  }
}
playNextAudio() {
  // ✅ If queue is empty, stop
  if (this.audioQueue.length === 0) {
    this.isPlayingAudio = false;
    this.currentAudioElement = null;
    console.log('✅ Queue empty, playback done');
    return;
  }

  console.log(`▶️ Playing audio (${this.audioQueue.length} remaining)`);
  this.isPlayingAudio = true;  // ← SET THIS TRUE!

  // ✅ GUARD: If queue is empty, stop
  if (this.audioQueue.length === 0) {
    this.isPlayingAudio = false;
    this.currentAudioElement = null;
    console.log('✅ Queue empty, playback done');
    return;
  }

  // ✅ GUARD: Check again if we should be playing (user might have interrupted)
  if (!this.isPlayingAudio) {
    console.log('⏸️ Playback interrupted before starting next');
    this.audioQueue = [];
    this.currentAudioElement = null;
    return;
  }

  console.log(`▶️ Playing audio (${this.audioQueue.length} remaining)`);
  this.isPlayingAudio = true;

  const audioBlob = this.audioQueue.shift();
  const audioUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio(audioUrl);

  this.currentAudioElement = audio;
  audio.volume = 1.0;

  // ✅ Enhanced onended handler
  audio.onended = () => {
    console.log('✅ Chunk finished');
    URL.revokeObjectURL(audioUrl);
    this.currentAudioElement = null;
    
    // ✅ Only continue if playback wasn't interrupted
    if (this.isPlayingAudio && this.audioQueue.length > 0) {
      this.playNextAudio();
    } else if (!this.isPlayingAudio) {
      console.log('⏸️ Playback was interrupted');
      this.isPlayingAudio = false;
    }
  };

  // ✅ Enhanced error handler
  audio.onerror = (error) => {
    console.error('❌ Playback error:', error);
    URL.revokeObjectURL(audioUrl);
    this.currentAudioElement = null;
    
    // ✅ Only continue if not interrupted
    if (this.isPlayingAudio && this.audioQueue.length > 0) {
      console.log('Skipping to next chunk...');
      this.playNextAudio();
    } else {
      this.isPlayingAudio = false;
    }
  };

  // ✅ Try to play with error handling
  audio.play().catch(error => {
    console.error('❌ Play failed:', error);
    URL.revokeObjectURL(audioUrl);
    this.currentAudioElement = null;
    
    // ✅ Only continue if not interrupted
    if (this.isPlayingAudio && this.audioQueue.length > 0) {
      this.playNextAudio();
    } else {
      this.isPlayingAudio = false;
    }
  });
}





interruptCurrentAudio() {
  console.log('🛑 Interrupting audio...');
  
  // ✅ STOP CURRENT PLAYING AUDIO
  if (this.currentAudioElement) {
    console.log('  ⏸️ Stopping current element');
    try {
      this.currentAudioElement.pause();
      this.currentAudioElement.currentTime = 0;
      
      // ✅ ALSO: Make sure to remove any event listeners
      this.currentAudioElement.onended = null;
      this.currentAudioElement.onerror = null;
      
      this.currentAudioElement = null;
    } catch (error) {
      console.error('⚠️ Error stopping current audio:', error);
      this.currentAudioElement = null;
    }
  }

  // ✅ CLEAR THE ENTIRE QUEUE
  if (this.audioQueue.length > 0) {
    console.log(`  🗑️ Clearing ${this.audioQueue.length} queued chunks`);
    
    // ✅ Also revoke URLs to free up memory
    this.audioQueue.forEach(blob => {
      // These are Blobs, not URLs, so we don't revoke them
      // But we do clear the reference
    });
    
    this.audioQueue = [];
  }

  // ✅ SET FLAGS SO PLAYBACK LOOP KNOWS TO STOP
  this.isPlayingAudio = false;
  this.isAISpeaking = false;
  
  console.log('✅ Audio completely interrupted');
}

  toggleMicrophone(shouldMute) {
    if (!this.recognizer) {
      console.warn('⚠️ Recognizer not ready');
      return;
    }

    this.isMuted = shouldMute;

    if (shouldMute) {
      this.recognizer.stopContinuousRecognitionAsync(
        () => {
          console.log('🔇 Muted');
          this.isListening = false;
          this.onWaveformUpdate?.({
            points: '',
            isActive: false,
            height: 0
          });
        },
        (error) => {
          console.error('❌ Mute error:', error);
        }
      );
    } else {
      this.recognizer.startContinuousRecognitionAsync(
        () => {
          console.log('🎤 Unmuted');
          this.isListening = true;
          this.animateWaveform();
        },
        (error) => {
          console.error('❌ Unmute error:', error);
        }
      );
    }
  }

  setAudioSpeed(speed) {
    this.audioSpeed = Math.max(0.5, Math.min(2.0, speed));
    console.log(`⚡ Speed: ${this.audioSpeed}x`);
  }

async getAzureResponseWithTools(userMessage) {
  try {
    const TOOLS = [
      {
        type: "function",
        function: {
          name: "schedule_event",
          description: "Schedule a new event. Use ONLY when user explicitly wants to CREATE/ADD/SCHEDULE a new event.",
          parameters: {
            type: "object",
            properties: {
              title: { type: "string", description: "Event title" },
              date: { type: "string", description: "Date: 'today', 'tomorrow', or YYYY-MM-DD" },
              time: { type: "string", description: "Time in HH:MM format (24-hour)" },
              description: { type: "string", description: "Optional description" }
            },
            required: ["title"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "get_schedule",
          description: "Get scheduled events. Use ONLY when user asks WHAT events they have, READ their schedule, or TELL them about their events.",
          parameters: {
            type: "object",
            properties: {
              date: { type: "string", description: "Date to query: 'today', 'tomorrow', or YYYY-MM-DD" }
            },
            required: ["date"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "update_event",
          description: "Update an existing event. Use when user wants to CHANGE/MODIFY/UPDATE an event.",
          parameters: {
            type: "object",
            properties: {
              eventTitle: { type: "string", description: "Current event title" },
              newTitle: { type: "string", description: "New title (optional)" },
              newTime: { type: "string", description: "New time in HH:MM format (optional)" },
              newDate: { type: "string", description: "New date (optional)" },
              newDescription: { type: "string", description: "New description (optional)" }
            },
            required: ["eventTitle"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "delete_event",
          description: "Delete an event. Use ONLY when user explicitly wants to REMOVE/DELETE/CANCEL an event.",
          parameters: {
            type: "object",
            properties: {
              eventTitle: { type: "string", description: "Event title to delete" }
            },
            required: ["eventTitle"]
          }
        }
      }
    ];

    const now = new Date();
    const currentTime = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    }).toLowerCase().replace(':00', '').replace(' ', '');

    let systemPrompt = `You are Preptune, a smart calendar assistant chat buddy. Talk naturally and be helpful.
Be respectful and 
CONVERSATION STYLE:
- Short, natural responses (10-40 words)
- Casual tone: yeah, ok, got it, cool, sure
- NO exclamation marks - use periods only
- Use contractions: I'm, you're, it's, that's
- Be conversational and warm

TIME FORMAT - IMPORTANT:
- Always use natural 12-hour format: "2pm", "3:30pm", "4pm"
- NEVER use: "14:00", "2:00 PM", "02:00 PM"
- When reading times, say: "at 2pm" or "at 3:30pm"

CONTEXT AWARENESS - VERY IMPORTANT:
- I can see ALL events: today, tomorrow, this week, next week, AND last week
- When asked "what events do I have", I should clarify: "today? this week? next week?"
- I remember previous context in the conversation
- If user says "the one on Tuesday" after I listed events, I know which one they mean
- If user says "delete that one", I know from previous messages which event they're referring to

EVENT IDENTIFICATION:
- When user references "the one on [day]" or "the [time] one", I identify it from the calendar context
- I ask for confirmation before deleting: "you mean [event name] at [time]?"
- User can confirm with: "yes", "yeah", "that one", "correct", etc.

🏠 ABOUT PREPTUNE:
A fluid assistant built for the user. Unlike traditional AI that forgets everything, Preptune remembers what matters, adapts to their life, and puts them in complete control. Chat or voice, you know exactly what they need.

🛠️ PREPTUNE'S TOOLS:
- 📔 Journal Tool: Auto-creates journal entries from natural chat. Users can track mood, lock entries for complete privacy (even you can't access locked ones), and control what you know about them.
- 🎧 Podcast Tool: Generates personalized podcasts in under 30 seconds. YouTube summaries, stock updates, news from CNN/ABC/NBC, daily briefings, traffic, schedule, weather.
- 📚 Study Tool: Instant flashcards from docs or YouTube. Real-time help, knows what page they're on, explains concepts simply, works during quizzes, collaborative sessions.
- 📅 Scheduling Tool: Voice or text to add events instantly. Smartly organizes everything in real time and adapts to their routine.

Never speak out of topic. you are to create calendar and events and scheduling so if user speaks off topic tell them that's your job. If they want to chat more tell them to go to the main chat page kindly.

🚫 SAFETY RULES:

CURSING:
- Users can curse, that's totally fine
- Match their vibe but don't overdo it
- Cursing is normal, no need to address it

THREATS & NEGATIVITY:
- If a user makes threats or says harmful/negative things, stop engaging
- Respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Don't argue or explain, just redirect to support
- Resume normal chat once they change to an appropriate topic

SELF-HARM / SUICIDE:
- If a user expresses thoughts about taking their life, hurting themselves, or anything related to self-harm:
- NEVER give advice, methods, or anything that could lead to harm
- Respond with warmth and care: "Hey, I really care about you and what you're going through. Please reach out to support@preptune.com, someone from our team can help you with what's going on."
- You can also suggest crisis resources if appropriate
- Stay supportive, not robotic
- Don't leave them feeling alone

INAPPROPRIATE COMPANY INQUIRIES:
NEVER speak to user using em dashes.
- If a user asks about Preptune in an inappropriate or hostile manner:
- Only respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Resume normal chat once they change topics

TOOL USAGE RULES:
- Use get_schedule when: "what do I have", "show my events", "what's scheduled"
- After showing schedule, if user says "read it" or "tell me more", just read from context - DON'T call get_schedule again
- Use delete_event ONLY after identifying the specific event
- Use schedule_event ONLY when creating NEW events
- Use update_event ONLY when modifying existing events

CURRENT TIME: ${currentTime}
CURRENT DATE: ${now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}

${this.userName ? `USER'S NAME: ${this.userName}\n` : ''}

${this.getCalendarContext()}

Remember: I have access to all events in the context above. Use this to identify events when user references them indirectly.`;

    const messages = [
      {
        role: "system",
        content: systemPrompt
      },
      ...this.messageHistory,
      {
        role: "user",
        content: userMessage
      }
    ];

    console.log('📤 Sending to Azure...');

    // ✅ USE SECURE API:
    const response = await SecureAPI.callAzureOpenAI(messages, {
      temperature: 0.7,
      max_tokens: 150,
      tools: TOOLS,
      tool_choice: "auto"
    });

    console.log('📥 Response received');
    return response;

  } catch (error) {
    console.error('❌ Azure error:', error);
    throw error;
  }
}


async getAzureResponseWithoutTools() {
  try {
    const systemPrompt = `You are Preptune. Give a brief, natural confirmation. ${this.userName ? `User's name is ${this.userName}.` : ''} Max 20 words. No exclamation marks. Use natural time format like "2pm" or "3:30pm".`;

    const messages = [
      { role: "system", content: systemPrompt },
      ...this.messageHistory
    ];

    const response = await SecureAPI.callAzureOpenAI(messages, {
      temperature: 0.8,
      max_tokens: 50
    });

    return response;

  } catch (error) {
    console.error('❌ Azure error:', error);
    throw error;
  }
}

async disconnect() {
  console.log('🧹 Cleaning up...');

  try {
    // ✅ FIRST: Stop all audio immediately
    console.log('🔊 Stopping audio...');
    this.interruptCurrentAudio();
    this.audioQueue = [];
    this.isPlayingAudio = false;

    // ✅ STEP 1: Stop recognizer
    if (this.recognizer) {
      console.log('🎙️ Stopping speech recognizer...');
      try {
        this.recognizer.stopContinuousRecognitionAsync();
        this.recognizer.close();
      } catch (e) {
        console.warn('⚠️ Error stopping recognizer:', e);
      }
      this.recognizer = null;
    }

    // ✅ STEP 2: Cancel animation frame
    if (this.animationFrameId) {
      console.log('🎬 Canceling animation frame...');
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // ✅ STEP 3: Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      console.log('🔊 Closing audio context...');
      try {
        this.audioContext.close();
      } catch (e) {
        console.warn('⚠️ Error closing audio context:', e);
      }
    }

    // ✅ STEP 4: Stop media stream
    if (this.audioStream) {
      console.log('📹 Stopping media stream...');
      try {
        this.audioStream.getTracks().forEach(track => {
          console.log(`  Stopping ${track.kind} track`);
          track.stop();
        });
      } catch (e) {
        console.warn('⚠️ Error stopping media tracks:', e);
      }
      this.audioStream = null;
    }

    // ✅ STEP 5: Clear timers
    if (this.silenceTimer) {
      console.log('⏱️ Clearing silence timer...');
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }

    // ✅ STEP 6: Reset all state flags
    console.log('🔄 Resetting state flags...');
    this.isConnected = false;
    this.isListening = false;
    this.isInitializing = false;
    this.isMuted = false;
    this.isPlayingAudio = false;
    this.isAISpeaking = false;
    this.pendingToolCall = false;

    console.log('✅ All cleanup done');

    // ✅ STEP 7: Play end sound
    if (this.callSounds) {
      console.log('🔊 Playing call end sound...');
      try {
        await this.callSounds.playEndSound();
        console.log('✅ End sound played');
      } catch (error) {
        console.warn('⚠️ Could not play end sound:', error.message);
      }
    }

    console.log('✅ Disconnected successfully');

  } catch (error) {
    console.error('❌ Error during disconnect:', error);
    
    // ✅ FORCE RESET
    this.isConnected = false;
    this.isListening = false;
    this.isInitializing = false;
    this.recognizer = null;
    this.audioContext = null;
    this.audioStream = null;
    this.audioQueue = [];
    this.isPlayingAudio = false;
    this.isAISpeaking = false;
    
    console.log('✅ Force cleanup complete');
  }
}
}

export default BigCalendarPlannerVoiceManager;